import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Locale, TranslationKeys } from './locales';
import { ko } from './ko';
import { en } from './en';
import { zh } from './zh';
import { ja } from './ja';
import { vi } from './vi';
import { es } from './es';

// 언어팩 매핑
const translations: Record<Locale, TranslationKeys> = {
  ko,
  en,
  zh,
  ja,
  vi,
  es,
};

// i18n 컨텍스트 타입
interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationKeys;
}

// i18n 컨텍스트 생성
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// i18n 제공자 컴포넌트
interface I18nProviderProps {
  children: ReactNode;
  defaultLocale?: Locale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({
  children,
  defaultLocale = 'ko',
}) => {
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  
  // html lang 동기화 및 CSS 변수 설정
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale);
      // 언어팩의 연도 단위를 CSS 변수로 설정
      const yearUnit = translations[locale].common.yearUnit;
      document.documentElement.style.setProperty('--year-unit', `"${yearUnit}"`);
    }
  }, [locale]);

  const value: I18nContextType = {
    locale,
    setLocale,
    t: translations[locale],
  };
  
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

// i18n 훅
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// 언어 설정을 로컬 스토리지에 저장
export const saveLocale = (locale: Locale): void => {
  localStorage.setItem('locale', locale);
};

// 언어 설정을 로컬 스토리지에서 불러오기
export const loadLocale = (): Locale => {
  const savedLocale = localStorage.getItem('locale');
  if (savedLocale && ['ko', 'en', 'zh', 'ja', 'vi', 'es'].includes(savedLocale)) {
    return savedLocale as Locale;
  }
  return 'ko'; // 기본값은 한국어
};

// 언어 설정 자동 저장 훅
export const useLocaleWithStorage = (): I18nContextType => {
  const context = useI18n();
  const { locale, setLocale, t } = context;
  
  const setLocaleWithStorage = (newLocale: Locale) => {
    setLocale(newLocale);
    saveLocale(newLocale);
  };
  
  return {
    locale,
    setLocale: setLocaleWithStorage,
    t,
  };
};

